import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { usePermission, useSession } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { StatCard } from "@how/ui";
import { type AudienceChannel, useAudienceCounts, useAudiences } from "./audience-queries";
import {
  CAMPAIGN_STATUS_COLOR,
  CAMPAIGN_STATUS_LABEL,
  type CampaignRow,
  useCampaignAction,
  useCampaignReport,
  useSaveCampaign,
  useScheduleCampaign,
  useTemplates,
} from "./communications-queries";
import { useEmailSenders } from "./sender-queries";

const EMPTY_DEFINITION = { filters: [] } as const;

/** "1 has …" or "3 have …". */
const people = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

/** "a", "a and b", "a, b, and c". */
const inWords = (parts: readonly string[]) =>
  parts.length <= 2
    ? parts.join(" and ")
    : `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1] ?? ""}`;

/**
 * Why people can't be reached, in words a church uses (#340): no number (or address) on their record, not agreed
 * (which includes having replied STOP or unsubscribed), children, who are never messaged, after an email
 * campaign, staging's made-up addresses, which are never emailed, and, while pilot texting is on (ADR 0072), numbers
 * that never opted in to the pilot.
 */
function unreachableReasons(
  channel: AudienceChannel,
  notAgreed: number,
  noAddress: number,
  children = 0,
  testAddress = 0,
  notPilotTester = 0,
): string[] {
  const texts = channel === "sms";
  const agreed = texts ? "agreed to texts (or replied STOP)" : "agreed to email (or unsubscribed)";
  const address = texts ? "mobile number" : "email address";
  return [
    ...(notAgreed > 0 ? [people(notAgreed, `hasn't ${agreed}`, `haven't ${agreed}`)] : []),
    ...(noAddress > 0
      ? [
          people(
            noAddress,
            `has no ${address} on their record`,
            `have no ${address} on their record`,
          ),
        ]
      : []),
    ...(children > 0
      ? [
          people(
            children,
            "is a child, who is never messaged",
            "are children, who are never messaged",
          ),
        ]
      : []),
    ...(testAddress > 0
      ? [
          people(
            testAddress,
            "is at a test address, never emailed",
            "are at a test address, never emailed",
          ),
        ]
      : []),
    ...(notPilotTester > 0
      ? [
          people(
            notPilotTester,
            "isn't a pilot tester (pilot texting reaches only them)",
            "aren't pilot testers (pilot texting reaches only them)",
          ),
        ]
      : []),
  ];
}

/**
 * One campaign, in the order a church works through it: what and to whom, then the dry run ("this reaches 412 of
 * 1,208 people", with a warning naming who won't get it and why, or a block when nobody can be reached), then the second signature and the send. Which
 * buttons appear depends on the campaign's status and the caller's permissions; the database refuses the rest, in
 * its own words.
 */
export function CampaignModal({
  campaign,
  onClose,
}: {
  readonly campaign: CampaignRow | null;
  readonly onClose: () => void;
}) {
  const session = useSession();
  const myId = session.status === "signed_in" ? session.session.user.id : null;
  const canApprove = usePermission("communications.approve", { write: true });
  const canSend = usePermission("communications.send", { write: true });
  const canCompose = usePermission("communications.compose", { write: true });
  const [name, setName] = useState(campaign?.name ?? "");
  const [channel, setChannel] = useState<AudienceChannel>(campaign?.channel ?? "sms");
  const [templateId, setTemplateId] = useState<string | null>(campaign?.templateId ?? null);
  const [audienceId, setAudienceId] = useState<string | null>(campaign?.audienceId ?? null);
  const [senderId, setSenderId] = useState<string | null>(campaign?.senderId ?? null);
  const senders = useEmailSenders();
  const [scheduleAt, setScheduleAt] = useState("");
  const [queued, setQueued] = useState<number | null>(null);
  const save = useSaveCampaign();
  const act = useCampaignAction();
  const schedule = useScheduleCampaign();
  const templates = useTemplates();
  const audiences = useAudiences();

  const status = campaign?.status ?? "draft";
  const editable = status === "draft" || status === "pending_approval" || status === "approved";
  const audience = (audiences.data ?? []).find((a) => a.id === audienceId) ?? null;
  const counts = useAudienceCounts(audience?.definition ?? EMPTY_DEFINITION, channel);
  const message = channel === "sms" ? "text" : "email";
  // Before sending: who in the audience won't get it. Nobody at all blocks the send (#340).
  const reach = editable && audience !== null ? (counts.data ?? null) : null;
  const nobodyReachable = reach !== null && reach.reachable === 0;
  const unreachable = reach === null ? 0 : reach.matched - reach.reachable;
  const live = status === "scheduled" || status === "sending";
  const report = useCampaignReport(
    campaign !== null && (live || status === "sent") ? campaign.id : null,
    live,
  );
  const dirty =
    campaign === null ||
    name.trim() !== campaign.name ||
    templateId !== campaign.templateId ||
    audienceId !== campaign.audienceId ||
    (channel === "email" && senderId !== campaign.senderId);
  const isAuthor = campaign !== null && myId !== null && myId === campaign.createdBy;
  const busy = save.isPending || act.isPending || schedule.isPending;
  const error = save.error ?? act.error ?? schedule.error;

  const run = (action: "submit" | "approve" | "cancel" | "delete") => {
    if (campaign === null) return;
    act.mutate(
      { campaignId: campaign.id, action },
      action === "delete" ? { onSuccess: onClose } : {},
    );
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={
        campaign === null ? (
          "New campaign"
        ) : (
          <Group gap="xs">
            <span>{campaign.name}</span>
            <Badge variant="light" color={CAMPAIGN_STATUS_COLOR[status]}>
              {CAMPAIGN_STATUS_LABEL[status]}
            </Badge>
          </Group>
        )
      }
      size="xl"
    >
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Name"
            required
            disabled={!editable}
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Channel
            </Text>
            <SegmentedControl
              disabled={campaign !== null}
              value={channel}
              onChange={(value) => {
                setChannel(value === "email" ? "email" : "sms");
                setTemplateId(null);
              }}
              data={[
                { value: "sms", label: "Text" },
                { value: "email", label: "Email" },
              ]}
            />
          </Stack>
          <Select
            label="Template"
            placeholder="What it says"
            disabled={!editable}
            data={(templates.data ?? [])
              .filter((t) => t.channel === channel && (t.isActive || t.id === templateId))
              .map((t) => ({ value: t.id, label: t.name }))}
            value={templateId}
            onChange={setTemplateId}
          />
          <Select
            label="Audience"
            placeholder="Who it goes to"
            disabled={!editable}
            data={(audiences.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
            value={audienceId}
            onChange={setAudienceId}
          />
          {channel === "email" ? (
            <Select
              label="Sent as"
              placeholder="The church's default sender"
              description="Who it appears to come from; replies go to them."
              disabled={!editable}
              clearable
              data={(senders.data ?? []).map((s) => ({
                value: s.id,
                label: s.verifiedAt === null ? `${s.name} (not confirmed yet)` : s.name,
                disabled: s.verifiedAt === null,
              }))}
              value={senderId}
              onChange={setSenderId}
            />
          ) : null}
        </SimpleGrid>

        {campaign?.bodySnapshot !== null && campaign?.bodySnapshot !== undefined ? (
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              What was approved
            </Text>
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {campaign.bodySnapshot}
            </Text>
          </Stack>
        ) : null}

        {audience === null ? null : (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Who this reaches today
            </Text>
            <Text size="sm">
              This reaches <strong>{(counts.data?.reachable ?? 0).toLocaleString()}</strong> of{" "}
              {(counts.data?.matched ?? 0).toLocaleString()} people in "{audience.name}".
            </Text>
            {/* Who it doesn't reach, and why, is the warning's to say (#340). */}
            {reach === null ? null : nobodyReachable ? (
              <Alert color={semanticColor("danger")} variant="light">
                {`Nobody in "${audience.name}" can get this ${message}: ${
                  reach.matched === 0
                    ? "the audience has nobody in it today"
                    : inWords(
                        unreachableReasons(
                          channel,
                          reach.notAgreed,
                          reach.noAddress,
                          reach.children,
                        ),
                      )
                }.${
                  reach.notAgreed + reach.noAddress > 0
                    ? ` Add ${channel === "sms" ? "a mobile number" : "an email address"} or their agreement on their record, or choose another audience.`
                    : ""
                }`}
              </Alert>
            ) : unreachable > 0 ? (
              <Alert color={semanticColor("warning")} variant="light">
                {`${unreachable.toLocaleString()} of ${reach.matched.toLocaleString()} won't get this ${message}: ${inWords(
                  unreachableReasons(channel, reach.notAgreed, reach.noAddress, reach.children),
                )}.`}
              </Alert>
            ) : null}
          </Stack>
        )}

        {report.data === undefined ? null : (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              What happened
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
              <StatCard label="Recipients" value={report.data.recipients.toLocaleString()} />
              <StatCard label="Delivered" value={report.data.delivered.toLocaleString()} />
              <StatCard
                label="Waiting"
                value={(report.data.queued + report.data.sent).toLocaleString()}
              />
              <StatCard label="Failed" value={report.data.failed.toLocaleString()} />
            </SimpleGrid>
            {report.data.blocked > 0 ? (
              <Alert color={semanticColor("warning")} variant="light">
                {`${report.data.blocked.toLocaleString()} skipped: ${
                  inWords(
                    unreachableReasons(
                      channel,
                      report.data.noConsent,
                      report.data.noAddress,
                      0,
                      report.data.testAddress,
                      report.data.notPilotTester,
                    ),
                  ) || "they could not be reached"
                }.`}
              </Alert>
            ) : null}
            {channel === "sms" ? (
              <Text size="xs" c="dimmed">
                {report.data.segments.toLocaleString()} text segments in all.
              </Text>
            ) : null}
          </Stack>
        )}

        {queued === null ? null : (
          <Alert color="teal" variant="light">
            Scheduled: {queued.toLocaleString()} messages queued.
          </Alert>
        )}
        {error === null ? null : (
          <Alert color="red" variant="light">
            {error.message}
          </Alert>
        )}

        <Group justify="space-between" align="flex-end">
          <Group>
            {campaign !== null &&
            canCompose.data === true &&
            (status === "draft" || status === "cancelled") ? (
              <Button
                variant="subtle"
                color="red"
                disabled={busy}
                onClick={() => {
                  run("delete");
                }}
              >
                Delete
              </Button>
            ) : null}
            {campaign !== null && canCompose.data === true && editable && status !== "draft" ? (
              <Button
                variant="subtle"
                disabled={busy}
                onClick={() => {
                  run("cancel");
                }}
              >
                Cancel campaign
              </Button>
            ) : null}
            {campaign !== null && canCompose.data === true && status === "scheduled" ? (
              <Button
                variant="subtle"
                disabled={busy}
                onClick={() => {
                  run("cancel");
                }}
              >
                Cancel the send
              </Button>
            ) : null}
          </Group>
          <Group>
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
            {editable && canCompose.data === true && dirty ? (
              <Button
                loading={save.isPending}
                disabled={name.trim() === "" || busy}
                onClick={() => {
                  save.mutate(
                    {
                      campaignId: campaign?.id ?? null,
                      name: name.trim(),
                      channel,
                      templateId,
                      audienceId,
                      senderId: channel === "email" ? senderId : null,
                    },
                    campaign === null ? { onSuccess: onClose } : {},
                  );
                }}
              >
                {campaign === null ? "Create draft" : "Save changes"}
              </Button>
            ) : null}
            {campaign !== null && status === "draft" && !dirty && canCompose.data === true ? (
              <Button
                variant="light"
                // Nobody to approve it for: the warning above says why (#378 item 4).
                disabled={busy || templateId === null || audienceId === null || nobodyReachable}
                onClick={() => {
                  run("submit");
                }}
              >
                Submit for approval
              </Button>
            ) : null}
            {campaign !== null &&
            status === "pending_approval" &&
            !dirty &&
            canApprove.data === true ? (
              <Button
                variant="light"
                disabled={busy || isAuthor}
                title={
                  isAuthor ? "A campaign is approved by someone other than its author" : undefined
                }
                onClick={() => {
                  run("approve");
                }}
              >
                Approve
              </Button>
            ) : null}
            {campaign !== null && editable && !dirty && canSend.data === true ? (
              <Group gap="xs" align="flex-end">
                <TextInput
                  label="Send at"
                  type="datetime-local"
                  size="xs"
                  value={scheduleAt}
                  onChange={(event) => {
                    setScheduleAt(event.currentTarget.value);
                  }}
                />
                <Button
                  loading={schedule.isPending}
                  disabled={busy || templateId === null || audienceId === null || nobodyReachable}
                  onClick={() => {
                    schedule.mutate(
                      { campaignId: campaign.id, at: scheduleAt === "" ? null : scheduleAt },
                      { onSuccess: setQueued },
                    );
                  }}
                >
                  {scheduleAt === "" ? "Send now" : "Schedule"}
                </Button>
              </Group>
            ) : null}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
